import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../../../../app.service';
import { CourseModuleSectionModel } from '../../../../models/course-module-section.model';
import { ApiRequestService } from '../../../../shared/api-request.service';
import { UtilsService } from '../../../../shared/utils.service';
import { RBACService } from '../../../../shared/rbac.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-courses-modules-sections-edit',
  templateUrl: './courses-modules-sections-edit.component.html',
  styleUrls: ['./courses-modules-sections-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesModulesSectionsEditComponent implements OnInit {
  course_id: number; // used for navigation after adding a record
  module_id: number;

  section: CourseModuleSectionModel = new CourseModuleSectionModel();

  inProgress = false;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      section: CourseModuleSectionModel;
      course_id: number;
      module_id: number;
    },
    private dialogRef: MatDialogRef<CoursesModulesSectionsEditComponent>
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    this.course_id = this.data.course_id;
    this.module_id = this.data.module_id;
    this.section = this.data.section;
  }

  onSubmit(form: NgForm) {
    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Error',
        'Please make sure you enter all fields with valid information.'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    this.section.id ? this.update() : this.create();
  }

  create() {
    this.api
      .makeRequest(
        'post',
        `v1/inductions/${this.course_id}/modules/${this.module_id}/sections`,
        this.section
      )
      .then((response: any) => {
        this.utils.showToast('The section was added');
        this.dialogRef.close(true);
        this.inProgress = false;
      })
      .catch((err: any) => {
        this.inProgress = false;
      });
  }

  update() {
    this.api
      .makeRequest(
        'put',
        `v1/inductions/${this.course_id}/modules/${this.module_id}/sections/${
          this.section.id
        }`,
        this.section
      )
      .then((response: any) => {
        this.utils.showToast('The section was updated');
        this.dialogRef.close(true);
        this.inProgress = false;
      })
      .catch((err: any) => {
        this.inProgress = false;
      });
  }

  onRemove() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction sections.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Sub-Section',
      'Are you sure you want to remove this sub-section?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${this.course_id}/modules/${
              this.module_id
            }/sections/${this.section.id}`
          )
          .then((response: any) => {
            this.utils.showToast('The sub-section was removed.');
            this.dialogRef.close(true);
          });
      }
    );
  }

  protected readonly RBACService = RBACService;
}
